import React, { useEffect, useState } from 'react';
import AuthImage from '../../components/Common/AuthImage';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Progress,
} from 'reactstrap';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import {
  dailyRecordsAPI,
  projectsAPI,
  vehiclesAPI,
  workTypesAPI,
} from '../../helpers/api';
import SideModal from '../../components/Common/SideModal';
import { useAuth } from '../../helpers/hooks';

interface SiteWork {
  id: string;
  targetQuantity: number | null;
  completedQuantity: number;
  progressPercentage: number | null;
  status: string;
  workType: {
    id: string;
    name: string;
    unit: string;
  };
}

interface DailyRecordFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
  recordId?: string | null;
}

const DailyRecordFormModal: React.FC<DailyRecordFormModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
  recordId,
}) => {
  const { user } = useAuth();
  const isEdit = !!recordId;
  const [activeTab, setActiveTab] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form verileri
  const [formData, setFormData] = useState({
    recordDate: new Date().toISOString().split('T')[0],
    siteId: '',
    notes: '',
  });

  // Seçenekler
  const [sites, setSites] = useState<any[]>([]);
  const [sitePersonnel, setSitePersonnel] = useState<any[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [allVehicles, setAllVehicles] = useState<any[]>([]);
  const [siteWorks, setSiteWorks] = useState<SiteWork[]>([]);
  const [loadingSiteWorks, setLoadingSiteWorks] = useState(false);

  // Seçimler
  const [selectedPersonnel, setSelectedPersonnel] = useState<
    Array<{ personnelId: string; dailyWage: number }>
  >([]);
  const [selectedVehicles, setSelectedVehicles] = useState<
    Array<{ vehicleId: string; usageType: string; driverId: string; driverType: string; notes: string }>
  >([]);
  const [selectedWorks, setSelectedWorks] = useState<
    Array<{ siteWorkId: string; quantity: number; notes: string }>
  >([]);

  // Fotoğraflar
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      resetForm();

      const loadData = async () => {
        await Promise.all([fetchSites(), fetchVehicles()]);

        if (recordId) {
          await loadExistingRecord(recordId);
        }

        setLoading(false);
      };

      loadData();
    }
  }, [isOpen, recordId]);

  // Şantiye seçildiğinde personel ve işleri getir
  useEffect(() => {
    if (formData.siteId && sites.length > 0) {
      fetchSitePersonnel(formData.siteId);
      fetchSiteWorks(formData.siteId);
    }
  }, [formData.siteId, sites]);

  const resetForm = () => {
    setFormData({
      recordDate: new Date().toISOString().split('T')[0],
      siteId: '',
      notes: '',
    });
    setSelectedPersonnel([]);
    setSelectedVehicles([]);
    setSelectedWorks([]);
    setSitePersonnel([]);
    setSiteWorks([]);
    setSelectedPhotos([]);
    setPhotoPreviews([]);
    setExistingPhotos([]);
    setActiveTab('1');
  };

  const loadExistingRecord = async (id: string) => {
    try {
      const response = await dailyRecordsAPI.getOne(id);
      const record = response.data;

      setFormData({
        recordDate: record.recordDate.split('T')[0],
        siteId: record.site?.id || '',
        notes: record.notes || '',
      });

      setSelectedPersonnel(
        record.personnelEntries.map((entry: any) => ({
          personnelId: entry.personnel.id,
          dailyWage: entry.dailyWage,
        }))
      );

      setSelectedVehicles(
        record.vehicleEntries.map((entry: any) => ({
          vehicleId: entry.vehicle.id,
          usageType: entry.usageType,
          driverId: entry.driverId || '',
          driverType: entry.driverType || '',
          notes: entry.notes || '',
        }))
      );

      setSelectedWorks(
        record.workEntries.map((entry: any) => ({
          siteWorkId: entry.siteWork.id,
          quantity: entry.quantity,
          notes: entry.notes || '',
        }))
      );

      if (record.photos) {
        setExistingPhotos(record.photos);
      }
    } catch (error) {
      console.error('Record load error:', error);
      toast.error('Kayıt yüklenemedi');
    }
  };

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchSitePersonnel = async (siteId: string) => {
    try {
      setLoadingPersonnel(true);
      const response = await projectsAPI.getSitePersonnel(siteId);
      setSitePersonnel(response.data.map((sp: any) => sp.personnel));
    } catch (error) {
      console.error('Site personnel fetch error:', error);
      setSitePersonnel([]);
    } finally {
      setLoadingPersonnel(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesAPI.getAll();
      setAllVehicles(response.data.filter((v: any) => v.isActive));
    } catch (error) {
      console.error('Vehicles fetch error:', error);
    }
  };

  const fetchSiteWorks = async (siteId: string) => {
    try {
      setLoadingSiteWorks(true);
      const response = await workTypesAPI.getSiteWorks(siteId);
      setSiteWorks(response.data.filter((sw: SiteWork) => sw.status !== 'COMPLETED'));
    } catch (error) {
      console.error('Site works fetch error:', error);
    } finally {
      setLoadingSiteWorks(false);
    }
  };

  // Sürücü seçenekleri
  const getDriverOptions = () => {
    const options: Array<{ id: string; name: string; type: 'USER' | 'PERSONNEL' }> = [];

    if (user) {
      options.push({
        id: user.id,
        name: `${user.firstName} ${user.lastName} (Ben)`,
        type: 'USER',
      });
    }

    selectedPersonnel.forEach((sp) => {
      const person = sitePersonnel.find((p) => p.id === sp.personnelId);
      if (person) {
        options.push({
          id: person.id,
          name: `${person.firstName} ${person.lastName}`,
          type: 'PERSONNEL',
        });
      }
    });

    return options;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.siteId) {
      toast.error('Lütfen şantiye seçin');
      return;
    }

    // İş miktarı kontrolü
    const invalidWorks = selectedWorks.filter((w) => !w.quantity || w.quantity <= 0);
    if (invalidWorks.length > 0) {
      toast.error('Seçilen işlerin miktarını girin');
      setActiveTab('3');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        recordDate: new Date(formData.recordDate),
        siteId: formData.siteId,
        notes: formData.notes || undefined,
        personnelEntries: selectedPersonnel,
        vehicleEntries: selectedVehicles,
        workEntries: selectedWorks.map((w) => ({
          siteWorkId: w.siteWorkId,
          quantity: w.quantity,
          notes: w.notes || undefined,
        })),
      };

      let savedRecordId = recordId;

      if (isEdit && recordId) {
        await dailyRecordsAPI.update(recordId, payload);
      } else {
        const response = await dailyRecordsAPI.create(payload);
        savedRecordId = response.data.id;
      }

      // Fotoğrafları yükle
      if (selectedPhotos.length > 0 && savedRecordId) {
        const uploadPromises = selectedPhotos.map((photo) => {
          const formDataPhoto = new FormData();
          formDataPhoto.append('file', photo);
          return dailyRecordsAPI.uploadPhoto(savedRecordId!, formDataPhoto);
        });

        try {
          await Promise.all(uploadPromises);
          toast.success(
            isEdit
              ? `Kayıt güncellendi ve ${selectedPhotos.length} fotoğraf eklendi`
              : `Kayıt oluşturuldu ve ${selectedPhotos.length} fotoğraf eklendi`
          );
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          toast.warning('Kayıt kaydedildi ama bazı fotoğraflar yüklenemedi');
        }
      } else {
        toast.success(isEdit ? 'Günlük kayıt güncellendi' : 'Günlük kayıt oluşturuldu');
      }

      toggle();
      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  // Personel işlemleri
  const togglePersonnel = (personnelId: string) => {
    const exists = selectedPersonnel.find((p) => p.personnelId === personnelId);
    if (exists) {
      setSelectedPersonnel(selectedPersonnel.filter((p) => p.personnelId !== personnelId));
      // Bu personel bir aracın sürücüsüyse, o araçtan da kaldır
      setSelectedVehicles(
        selectedVehicles.map((v) =>
          v.driverId === personnelId ? { ...v, driverId: '', driverType: '' } : v
        )
      );
    } else {
      setSelectedPersonnel([...selectedPersonnel, { personnelId, dailyWage: 1 }]);
    }
  };

  const updatePersonnelWage = (personnelId: string, dailyWage: number) => {
    setSelectedPersonnel(
      selectedPersonnel.map((p) =>
        p.personnelId === personnelId ? { ...p, dailyWage } : p
      )
    );
  };

  // Araç işlemleri
  const toggleVehicle = (vehicleId: string) => {
    const exists = selectedVehicles.find((v) => v.vehicleId === vehicleId);
    if (exists) {
      setSelectedVehicles(selectedVehicles.filter((v) => v.vehicleId !== vehicleId));
    } else {
      setSelectedVehicles([
        ...selectedVehicles,
        { vehicleId, usageType: 'FULL_DAY', driverId: '', driverType: '', notes: '' },
      ]);
    }
  };

  const updateVehicle = (vehicleId: string, field: string, value: string) => {
    if (field === 'driverId') {
      const driver = getDriverOptions().find((d) => d.id === value);
      setSelectedVehicles(
        selectedVehicles.map((v) =>
          v.vehicleId === vehicleId
            ? { ...v, driverId: value, driverType: driver?.type || '' }
            : v
        )
      );
    } else {
      setSelectedVehicles(
        selectedVehicles.map((v) =>
          v.vehicleId === vehicleId ? { ...v, [field]: value } : v
        )
      );
    }
  };

  // İş işlemleri
  const toggleWork = (siteWorkId: string) => {
    const exists = selectedWorks.find((w) => w.siteWorkId === siteWorkId);
    if (exists) {
      setSelectedWorks(selectedWorks.filter((w) => w.siteWorkId !== siteWorkId));
    } else {
      setSelectedWorks([...selectedWorks, { siteWorkId, quantity: 0, notes: '' }]);
    }
  };

  const updateWork = (siteWorkId: string, field: 'quantity' | 'notes', value: number | string) => {
    setSelectedWorks(
      selectedWorks.map((w) =>
        w.siteWorkId === siteWorkId ? { ...w, [field]: value } : w
      )
    );
  };

  // Fotoğraf işlemleri
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const maxPhotos = 10;
    const currentCount = selectedPhotos.length + existingPhotos.length;
    const remainingSlots = maxPhotos - currentCount;

    if (newFiles.length > remainingSlots) {
      toast.warning(`En fazla ${maxPhotos} fotoğraf ekleyebilirsiniz`);
    }

    const filesToAdd = newFiles.slice(0, remainingSlots);

    filesToAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    setSelectedPhotos((prev) => [...prev, ...filesToAdd]);
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    setSelectedPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper fonksiyonlar
  const isPersonnelSelected = (id: string) => selectedPersonnel.some((p) => p.personnelId === id);
  const isVehicleSelected = (id: string) => selectedVehicles.some((v) => v.vehicleId === id);
  const isWorkSelected = (id: string) => selectedWorks.some((w) => w.siteWorkId === id);
  const getPersonnelWage = (id: string) => selectedPersonnel.find((p) => p.personnelId === id)?.dailyWage || 1;
  const getVehicleData = (id: string) => selectedVehicles.find((v) => v.vehicleId === id);
  const getWorkQuantity = (id: string) => selectedWorks.find((w) => w.siteWorkId === id)?.quantity || 0;

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title={isEdit ? 'Günlük Kayıt Düzenle' : 'Yeni Günlük Kayıt'}
      size="lg"
    >
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Yükleniyor...</p>
        </div>
      ) : (
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
          <Form onSubmit={handleSubmit} className="daily-record-form">
            {/* Temel Bilgiler Card */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
              <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-information-line me-2"></i>
                Temel Bilgiler
              </div>
              <div>
              <FormGroup>
                <Label>Tarih *</Label>
                <Input
                  type="date"
                  value={formData.recordDate}
                  onChange={(e) => setFormData({ ...formData, recordDate: e.target.value })}
                  required
                  disabled={isEdit}
                />
              </FormGroup>
              <FormGroup>
                <Label>Şantiye *</Label>
                <Input
                  type="select"
                  value={formData.siteId}
                  onChange={(e) => setFormData({ ...formData, siteId: e.target.value })}
                  required
                  disabled={isEdit}
                >
                  <option value="">Şantiye Seçin</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.project.city.name} / {site.project.name} / {site.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup className="mb-0">
                <Label>Not</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Genel not..."
                />
              </FormGroup>
              </div>
            </div>

            {/* Sekmeler Card */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <Nav tabs className="form-tabs">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '1' })}
                  onClick={() => setActiveTab('1')}
                >
                  <i className="ri-team-line me-1"></i>
                  Personel
                  {selectedPersonnel.length > 0 && (
                    <span className="tab-badge">{selectedPersonnel.length}</span>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '2' })}
                  onClick={() => setActiveTab('2')}
                >
                  <i className="ri-truck-line me-1"></i>
                  Araçlar
                  {selectedVehicles.length > 0 && (
                    <span className="tab-badge">{selectedVehicles.length}</span>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '3' })}
                  onClick={() => setActiveTab('3')}
                >
                  <i className="ri-hammer-line me-1"></i>
                  İşler
                  {selectedWorks.length > 0 && (
                    <span className="tab-badge">{selectedWorks.length}</span>
                  )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '4' })}
                  onClick={() => setActiveTab('4')}
                >
                  <i className="ri-camera-line me-1"></i>
                  Foto
                  {(selectedPhotos.length + existingPhotos.length) > 0 && (
                    <span className="tab-badge">{selectedPhotos.length + existingPhotos.length}</span>
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* Personel Sekmesi */}
              <TabPane tabId="1">
                <div className="tab-section">
                  {!formData.siteId ? (
                    <p className="text-muted text-center py-3 mb-0">
                      <i className="ri-information-line me-1"></i>
                      Personel görmek için önce şantiye seçin
                    </p>
                  ) : loadingPersonnel ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" color="primary" />
                      <p className="mt-2 mb-0 text-muted">Yükleniyor...</p>
                    </div>
                  ) : sitePersonnel.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">
                      <i className="ri-user-unfollow-line me-1"></i>
                      Bu şantiyeye atanmış personel yok
                    </p>
                  ) : (
                    <div className="selectable-list">
                      {sitePersonnel.map((person) => (
                        <div
                          key={person.id}
                          className={`selectable-item ${isPersonnelSelected(person.id) ? 'selected' : ''}`}
                        >
                          <div className="selectable-item-main" onClick={() => togglePersonnel(person.id)}>
                            <div className="selectable-item-check">
                              <i className={isPersonnelSelected(person.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                            </div>
                            <div className="selectable-item-info">
                              <strong>{person.firstName} {person.lastName}</strong>
                              <small>{person.title || 'İşçi'}</small>
                            </div>
                          </div>
                          {isPersonnelSelected(person.id) && (
                            <div className="selectable-item-options">
                              <span className="option-label">Yevmiye:</span>
                              <div className="wage-buttons">
                                {[0.5, 1, 1.5, 2].map((wage) => (
                                  <button
                                    key={wage}
                                    type="button"
                                    className={`wage-btn ${getPersonnelWage(person.id) === wage ? 'active' : ''}`}
                                    onClick={() => updatePersonnelWage(person.id, wage)}
                                  >
                                    {wage}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabPane>

              {/* Araç Sekmesi */}
              <TabPane tabId="2">
                <div className="tab-section">
                  {allVehicles.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">
                      Aktif araç bulunamadı
                    </p>
                  ) : (
                    <div className="selectable-list">
                      {allVehicles.map((vehicle) => {
                        const vehicleData = getVehicleData(vehicle.id);
                        return (
                          <div
                            key={vehicle.id}
                            className={`selectable-item ${isVehicleSelected(vehicle.id) ? 'selected' : ''}`}
                          >
                            <div className="selectable-item-main" onClick={() => toggleVehicle(vehicle.id)}>
                              <div className="selectable-item-check">
                                <i className={isVehicleSelected(vehicle.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                              </div>
                              <div className="selectable-item-info">
                                <strong>{vehicle.plate}</strong>
                                <small>{vehicle.brand} {vehicle.model}</small>
                              </div>
                            </div>
                            {isVehicleSelected(vehicle.id) && vehicleData && (
                              <div className="selectable-item-options vehicle-options">
                                <div className="option-row">
                                  <span className="option-label">Kullanım:</span>
                                  <div className="usage-buttons">
                                    <button
                                      type="button"
                                      className={`usage-btn ${vehicleData.usageType === 'FULL_DAY' ? 'active' : ''}`}
                                      onClick={() => updateVehicle(vehicle.id, 'usageType', 'FULL_DAY')}
                                    >
                                      Tam Gün
                                    </button>
                                    <button
                                      type="button"
                                      className={`usage-btn ${vehicleData.usageType === 'HALF_DAY' ? 'active' : ''}`}
                                      onClick={() => updateVehicle(vehicle.id, 'usageType', 'HALF_DAY')}
                                    >
                                      Yarım Gün
                                    </button>
                                  </div>
                                </div>
                                <div className="option-row">
                                  <span className="option-label">Sürücü:</span>
                                  <Input
                                    type="select"
                                    bsSize="sm"
                                    value={vehicleData.driverId}
                                    onChange={(e) => updateVehicle(vehicle.id, 'driverId', e.target.value)}
                                  >
                                    <option value="">Sürücü seçin...</option>
                                    {getDriverOptions().map((driver) => (
                                      <option key={driver.id} value={driver.id}>
                                        {driver.name}
                                      </option>
                                    ))}
                                  </Input>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabPane>

              {/* İş Sekmesi */}
              <TabPane tabId="3">
                <div className="tab-section">
                  {!formData.siteId ? (
                    <p className="text-muted text-center py-3 mb-0">
                      <i className="ri-information-line me-1"></i>
                      İş görmek için önce şantiye seçin
                    </p>
                  ) : loadingSiteWorks ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" color="primary" />
                      <p className="mt-2 mb-0 text-muted">Yükleniyor...</p>
                    </div>
                  ) : siteWorks.length === 0 ? (
                    <p className="text-muted text-center py-3 mb-0">
                      <i className="ri-tools-line me-1"></i>
                      Bu şantiyeye atanmış iş yok
                    </p>
                  ) : (
                    <div className="selectable-list">
                      {siteWorks.map((work) => (
                        <div
                          key={work.id}
                          className={`selectable-item ${isWorkSelected(work.id) ? 'selected' : ''}`}
                        >
                          <div className="selectable-item-main" onClick={() => toggleWork(work.id)}>
                            <div className="selectable-item-check">
                              <i className={isWorkSelected(work.id) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                            </div>
                            <div className="selectable-item-info">
                              <strong>{work.workType.name}</strong>
                              <small>
                                {work.completedQuantity.toLocaleString('tr-TR')} {work.workType.unit}
                                {work.targetQuantity && (
                                  <> / {work.targetQuantity.toLocaleString('tr-TR')} {work.workType.unit}</>
                                )}
                              </small>
                              {work.targetQuantity && work.progressPercentage !== null && (
                                <Progress
                                  value={work.progressPercentage}
                                  className="mt-1"
                                  style={{ height: '4px' }}
                                  color={work.progressPercentage >= 100 ? 'success' : 'primary'}
                                />
                              )}
                            </div>
                          </div>
                          {isWorkSelected(work.id) && (
                            <div className="selectable-item-options work-options">
                              <div className="option-row">
                                <span className="option-label">Miktar ({work.workType.unit}):</span>
                                <Input
                                  type="number"
                                  bsSize="sm"
                                  min={0}
                                  step={0.01}
                                  placeholder="0"
                                  value={getWorkQuantity(work.id) || ''}
                                  onChange={(e) => updateWork(work.id, 'quantity', parseFloat(e.target.value) || 0)}
                                  style={{ width: '100px' }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabPane>

              {/* Fotoğraf Sekmesi */}
              <TabPane tabId="4">
                <div className="tab-section">
                  {/* Mevcut fotoğraflar (edit modunda) */}
                  {existingPhotos.length > 0 && (
                    <div className="existing-photos mb-3">
                      <label className="photo-label">Mevcut Fotoğraflar</label>
                      <div className="photo-grid">
                        {existingPhotos.map((photo) => (
                          <div key={photo.id} className="photo-item existing">
                            <AuthImage src={photo.filePath} alt={photo.description || 'Fotoğraf'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Yeni fotoğraf önizlemeleri */}
                  {photoPreviews.length > 0 && (
                    <div className="new-photos mb-3">
                      <label className="photo-label">Yeni Fotoğraflar</label>
                      <div className="photo-grid">
                        {photoPreviews.map((preview, index) => (
                          <div key={index} className="photo-item new">
                            <img src={preview} alt={`Yeni ${index + 1}`} />
                            <button
                              type="button"
                              className="photo-remove"
                              onClick={() => removePhoto(index)}
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fotoğraf ekleme */}
                  {(selectedPhotos.length + existingPhotos.length) < 10 && (
                    <div className="photo-upload-area">
                      <label className="photo-upload-btn">
                        <i className="ri-add-line"></i>
                        <span>Fotoğraf Ekle</span>
                        <small>{selectedPhotos.length + existingPhotos.length}/10</small>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handlePhotoSelect}
                          hidden
                        />
                      </label>
                    </div>
                  )}
                </div>
              </TabPane>
            </TabContent>
            </div>

            {/* Kaydet Butonu */}
            <div className="form-actions">
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {isEdit ? 'Güncelleniyor...' : 'Kaydediliyor...'}
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i>
                  {isEdit ? 'Güncelle' : 'Kaydet'}
                </>
              )}
            </Button>
            </div>
          </Form>
        </div>
      )}
    </SideModal>
  );
};

export default DailyRecordFormModal;
